import React, { useContext, useMemo, useState } from 'react';

// Third-parties.
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

// Helper.
import Helper from '../../../helpers/Helper';

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartSeries from '../../../types/ChartSeries';
import ChartPlotLine from '../../../types/ChartPlotLine';

// Constants.
import { DATA_INITIALISING, DATA_LOADING, DATA_LOADED, CHART_DATA_TYPE } from '../../Constants';
import { CHART_COLOURS_STATEMENTS } from '../../Constants';
import { VIEW_MODE } from '../../Constants';
import { useLicences } from "../../../context/LicenceContext";
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import ConditionalRender from "../../../helpers/ConditionalRender";
import HostApi from "../../../api/host/HostApi";

function ProcessorChart(props: { host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [loadingBasic, setLoadingBasic] = useState<number>(DATA_LOADING);
    const [loadingDetail, setLoadingDetail] = useState<number>(DATA_INITIALISING);

    const [timeSeries, setTimeSeries] = useState<number[][]>([]);
    const [series, setSeries] = useState<ChartSeries[]>([]);
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);

    const timeRangeContext = useContext(TimeRangeContext)
    const {licences} = useLicences();

    const timePeak = useMemo(() => {
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    void useMemo(async() => {
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, 'cpuutilisation', limit);
        setTimeSeries(data.map(item => {
            return [getConvertedTimeToUTC(item), item.avg]
        }));
        setLoadingBasic(DATA_LOADED);
    }, [timeRangeContext, props.host]);

    async function getData() {

        // Set the metrics we're going to retrieve via API calls.
        const metrics: string[] = ['non-nice user cpu ticks', 'nice user cpu ticks', 'system cpu ticks', 'IO-wait cpu ticks', 'IRQ cpu ticks', 'softirq cpu ticks', 'stolen cpu ticks'];

        let seriesData: any[] = [];
        // Get detailed data.
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        for (let index = 0; index < metrics.length; index++) {
            const metric = metrics[index];
            const data = await HostApi.getHostStatisticTime(buildApiQuery(timeRangeContext.timeRange), props.host.id, metric, limit);
            seriesData.push({
                name: metric,
                color: CHART_COLOURS_STATEMENTS[index],
                type: 'column',
                data: data.map(item => {
                    return [getConvertedTimeToUTC(item), item.avg]
                }),
                zIndex: 1
            });
        }
        setSeries(seriesData)
        // Mark the content as loaded.
        setLoadingDetail(DATA_LOADED);
    }

    const basicChart = useMemo(() => {
        const basicChartOptions = chartOptionsGranularity([
            {
                name: 'Average CPU',
                type: 'spline',
                minHeight: 3,
                data: timeSeries,
                zIndex: 1,
                yAxis: 1
            }], timePeak, {
            credits: {enabled: highchartsCredits(licences)},
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
                }
            },
            plotLines: props.plotLines || []
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={basicChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext, licences, props.plotLines]);

    const detailChart = useMemo(() => {

        const detailChartOptions = chartOptionsGranularity(
            series, 100, {
                credits: {enabled: highchartsCredits(licences)},
                timeRangeContext: timeRangeContext,
                legend: {
                    align: 'left',
                    backgroundColor: 'transparent',
                    enabled: false,
                    floating: false,
                    itemMarginTop: 0,
                    itemMarginBottom: 2,
                    itemStyle: {
                        fontSize: '.7rem'
                    },
                    layout: 'horizontal',
                    padding: 0,
                    x: 0,
                    y: 0,
                    verticalAlign: 'top'
                },
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
                    }
                },
                plotLines: props.plotLines || []
            })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [series, timePeak, timeRangeContext, licences, props.plotLines]);

    function toggleChart() {
        if (loadingDetail === DATA_INITIALISING) {
            setLoadingDetail(DATA_LOADING)
            getData();
        }

        // Toggle the displayed chart.
        setMode(((mode === VIEW_MODE.BASIC) ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC));
    }

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                Average CPU over time
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                   data-bs-target="#collapseProcessorCharts" aria-expanded="false"
                   aria-controls="collapseProcessorCharts"/>
                <div className="btn-group float-end">
                    <button type="button" className="btn btn-xsm btn-primary float-end ms-1"
                            onClick={toggleChart}>{((mode === VIEW_MODE.BASIC) ? 'Show Breakdown' : 'Show Summary')}</button>
                </div>
            </div>
            <div id="collapseProcessorCharts" className="card-body collapse show p-0">
                <div className="row row-cols-1">
                    <div className="col p-3">
                        <React.Fragment>
                            <h4 className='icons-chart-title'>
                                Average CPU
                                {timePeak !== null && (
                                    <span className="peak">Peak Average CPU {Math.round(timePeak)}%</span>
                                )}
                            </h4>

                            <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                                <ConditionalRender if={loadingBasic === DATA_LOADING}>
                                    <div className="w-100 text-center text-muted mt-3">
                                        <div className="loader spinner chartSpinner">
                                        </div>
                                        <p className="mt-3">
                                            Loading data...
                                        </p>
                                    </div>
                                </ConditionalRender>
                                <ConditionalRender if={loadingBasic === DATA_LOADED}>
                                    {basicChart}
                                </ConditionalRender>
                            </ConditionalRender>


                            <ConditionalRender if={mode === VIEW_MODE.DETAIL}>
                                <ConditionalRender if={loadingDetail === DATA_LOADING}>
                                    <div className="w-100 text-center text-muted mt-3">
                                        <div className="loader spinner chartSpinner">
                                        </div>
                                        <p className="mt-3">
                                            Loading data...
                                        </p>
                                    </div>
                                </ConditionalRender>
                                <ConditionalRender if={loadingDetail === DATA_LOADED}>
                                    {detailChart}
                                </ConditionalRender>
                            </ConditionalRender>

                        </React.Fragment>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProcessorChart;
